import React, { useState, forwardRef } from 'react';

import { nanoid } from '@reduxjs/toolkit';

import { bemCssModules, getTestId } from '@utils';

import Spinner from '@components/Spinner';
import Tooltip from '@components/Tooltip';

import EyeIcon from './eye_icon.svg';
import EyeOpenedIcon from './eye_opened_icon.svg';
import QuestionIcon from './question_icon.svg';

import style from './Input.scss';

const cn = bemCssModules(style, 'Input');
const ti = getTestId('Input');

export type TNewInput = {
  className?: string;
  label?: string;
  error?: boolean | React.ReactNode;
  isLoading?: boolean;
  tooltipContent?: React.ReactNode;
  button?: React.ReactNode;
  testId?: string;
  innerInputCustomClassName?: string;
} & React.InputHTMLAttributes<HTMLInputElement>;

const NewInput = forwardRef<HTMLInputElement, TNewInput>((props, ref) => {
  const {
    className,
    label,
    placeholder,
    value = '',
    onChange,
    disabled,
    error,
    isLoading,
    tooltipContent,
    button,
    testId,
    type,
    innerInputCustomClassName,
    ...rest
  } = props;

  const [id] = useState(nanoid());
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
  const toggleTooltip = () => {
    setTooltipIsOpen(!tooltipIsOpen);
  };
  const isErrorMessage = error && typeof error !== 'boolean';

  const isPasswordType = type === 'password';
  const [masked, setMasked] = useState(isPasswordType);

  const switchMasked = () => {
    setMasked(!masked);
  };

  return (
    <div className={cn(null, [className])}>
      {label && (
        <label className={cn('label')} htmlFor={id}>
          {label}
        </label>
      )}
      <div className={cn('input-wrapper')}>
        <input
          className={cn(
            'input',
            {
              withLabel: !!label,
              error: !!error,
              isPasswordType,
            },
            [innerInputCustomClassName]
          )}
          ref={ref}
          id={id}
          type={isPasswordType && masked ? 'password' : 'text'}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          disabled={disabled || isLoading}
          data-testid={ti(testId)}
          {...rest}
        />
        <span className={cn('gradient', { isPasswordType })} />
        {isLoading && !isPasswordType && (
          <Spinner className={cn('loader')} color="var(--color-action-select-borders)" size={12} />
        )}
        {isPasswordType && (
          <button
            className={cn('switch-masked', { withLabel: !!label })}
            type="button"
            onClick={switchMasked}
          >
            {masked ? <EyeIcon /> : <EyeOpenedIcon />}
          </button>
        )}
      </div>
      <div className={cn('error-wrapper', { active: isErrorMessage })}>
        {isErrorMessage ? <div className={cn('error')}>{error}</div> : button}
        {tooltipContent && (
          <div className={cn('tooltip-wrapper')}>
            <button className={cn('tooltip-button')} type="button" onClick={toggleTooltip}>
              <QuestionIcon />
            </button>
            <Tooltip className={cn('tooltip')} isOpen={tooltipIsOpen}>
              {tooltipContent}
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
});

export default NewInput;
